// @ts-nocheck
import { useEffect, useState } from 'react';
import { AuthCallback } from './pages/AuthCallback';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Layout } from './components/Layout';
import { SupabaseSync } from './lib/supabase-sync';
import Dashboard from './pages/Dashboard';
import BillingProfile from './pages/BillingProfile';
import Leads from './pages/Leads';
import MapView from './pages/MapView';
import Team from './pages/Team';
import Tasks from './pages/Tasks';
import Chat from './pages/Chat';
import Import from './pages/Import';
import Contracts from './pages/Contracts';
import { ContractSigning } from './pages/ContractSigning';
import ContractDetail from './pages/ContractDetail';
import { Properties } from './pages/Properties';
import { PropertyDetail } from './pages/PropertyDetail';
import { Maintenance } from './pages/Maintenance';
import { RentDashboard } from './pages/RentDashboard';
import Deals from './pages/Deals';
import Tenants from './pages/Tenants';
import Portfolio from './pages/Portfolio';
import Contractors from './pages/Contractors';
import Analytics from './pages/Analytics';
import Calculator from './pages/Calculator';
import SettingsPage from './pages/SettingsPage';
import Login from './pages/Login';
import EmailConfirmed from './pages/EmailConfirmed';
import TeamSelection from './pages/TeamSelection';
import AutomationsHub from './pages/AutomationsHub';
import ResetPassword from './pages/ResetPassword';
import Onboarding from './pages/Onboarding';
import ContractorPortal from './pages/ContractorPortal';
import TenantPortal from './pages/TenantPortal';
import ClientPortal from './pages/ClientPortal';

import { useStore } from './store/useStore';
import { supabase, isSupabaseConfigured } from './lib/supabase';
import { Building2, Loader2 } from 'lucide-react';
import Calendar from './pages/Calendar';
import AITest from './pages/AITest';
import AISettings from './pages/AISettings';
import SMSSettings from './pages/SMSSettings';
import SMSInbox from './pages/SMSInbox';
import NotificationInbox from './pages/NotificationInbox';
import EmailInbox from './pages/EmailInbox';
import AgentProfile from './pages/AgentProfile';
import LeadManagement from './pages/LeadManagement';
import { startSMSPolling, stopSMSPolling } from './lib/sms-polling';
import AdminDashboard from './pages/AdminDashboard';
import AdminPromos from './pages/AdminPromos';
import { Toaster } from 'react-hot-toast';


import { MarketingLayout } from './components/MarketingLayout';
import { DeepSpaceLoader } from './components/DeepSpaceLoader';
import Home from './pages/marketing/Home';
import Pricing from './pages/marketing/Pricing';
import About from './pages/marketing/About';
import Privacy from './pages/marketing/Privacy';
import Terms from './pages/marketing/Terms';
import Contact from './pages/marketing/Contact';
import Blog from './pages/marketing/Blog';
import LeadShare from './pages/marketing/LeadShare';
import LeadShareEditor from './pages/LeadShareEditor';
import CRMCompare from './pages/marketing/CRMCompare';
import Integrations from './pages/marketing/Integrations';
import ScrollToTop from './components/ScrollToTop';
import AITrainingStudio from './pages/AITrainingStudio';


function ProtectedRoute({ children, checking, isMfaRequired }: { children: React.ReactNode, checking?: boolean, isMfaRequired: boolean }) {
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const currentUser = useStore((s) => s.currentUser);
  
  if (checking) return <DeepSpaceLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Only redirect to login for MFA if we are NOT already on the login page
  if (isMfaRequired) {
    const isLoginPage = window.location.pathname === '/login';
    if (!isLoginPage) {
      console.log('[App] MFA required, redirecting to login');
      return <Navigate to="/login" replace />;
    }
  }

  if (isSupabaseConfigured && !currentUser) {
    return <DeepSpaceLoader status="Loading user profile..." />;
  }

  const currentPath = window.location.pathname;
  const isOnboardingPage = currentPath === '/onboarding';

  if (currentUser) {
    // If a user somehow navigates to /onboarding while logged in (and NOT a new signup), redirect to /dashboard
    if (isOnboardingPage) {
      const isNewSignup = localStorage.getItem('wholescale-new-signup') === 'true';
      if (!isNewSignup) {
        console.log('[Onboarding] Existing user → redirecting to dashboard');
        return <Navigate to="/dashboard" replace />;
      }
    }

    const isContractor = currentUser.role === 'contractor';
    const isTenant = currentUser.role === 'tenant';
    const isBuyer = currentUser.role === 'buyer';
    const isSeller = currentUser.role === 'seller';

    if (isContractor) {
      if (!currentPath.startsWith('/contractor-portal/')) {
        console.log('[App] Contractor role detected, redirecting to contractor-portal');
        return <Navigate to={`/contractor-portal/${currentUser.id}`} replace />;
      }
    } else if (isTenant) {
      if (!currentPath.startsWith('/tenant-portal/')) {
        console.log('[App] Tenant role detected, redirecting to tenant-portal');
        return <Navigate to={`/tenant-portal/${currentUser.id}`} replace />;
      }
    } else if (isBuyer || isSeller) {
      if (currentPath !== '/client-portal' && currentPath !== '/onboarding') {
        console.log('[App] Buyer/Seller role detected, redirecting to client-portal');
        return <Navigate to="/client-portal" replace />;
      }
    } else {
      // CRM User gating (Agent, Property Manager, Investor, Other, etc.)
      if (currentPath.startsWith('/contractor-portal/') || currentPath.startsWith('/tenant-portal/') || currentPath === '/client-portal') {
        console.log('[App] CRM user on portal, redirecting to dashboard');
        return <Navigate to="/dashboard" replace />;
      }

      // If no team selected, redirect to team selection
      if (isSupabaseConfigured) {
        const hasTeam = localStorage.getItem('wholescale-preferred-team') || currentUser?.teamId;
        const isTeamSelectionPage = currentPath === '/team-selection';
        if (!hasTeam && !isTeamSelectionPage) {
          console.log('[App] No team selected, redirecting to team-selection');
          return <Navigate to="/team-selection" replace />;
        }
        if (hasTeam && isTeamSelectionPage) {
          console.log('[App] Team already selected, redirecting to dashboard');
          return <Navigate to="/dashboard" replace />;
        }
      }
    }
  }

  return <>{children}</>;
}

function PublicRoute({ children, checking, isMfaRequired }: { children: React.ReactNode, checking?: boolean, isMfaRequired: boolean }) {
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  if (checking) return <DeepSpaceLoader />;
  
  if (isAuthenticated && !isMfaRequired) {
    // If authenticated and no MFA needed, check team selection
    if (isSupabaseConfigured && !localStorage.getItem('wholescale-preferred-team')) {
      return <Navigate to="/team-selection" replace />;
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Wrapper to share SupabaseSync across routes without remounting
function DataSyncWrapper() {
  return (
    <SupabaseSync>
      <Outlet />
    </SupabaseSync>
  );
}

// Loading screen while checking session is no longer needed in App.tsx
// SupabaseSync will handle the branded loading experience.

export function App() {

  const [isChecking, setIsChecking] = useState(false);
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured);
  const [isMfaRequired, setIsMfaRequired] = useState(false);
  const { login, updateProfile, incrementLoginStreak, loadLeads } = useStore();

  const checking = isChecking || (isSupabaseConfigured && !authReady);

  useEffect(() => {
    // 0. Handle password recovery redirect immediately (Issue 10)
    const hash = window.location.hash;
    const search = window.location.search;
    if (hash.includes('type=recovery') || search.includes('type=recovery')) {
      if (window.location.pathname !== '/auth/callback') {
        console.log('[App] Recovery link detected, redirecting to AuthCallback...');
        window.location.href = `/auth/callback${search}${hash}`;
      }
    }
  }, []);

  const updateMfaStatus = async () => {
    if (!isSupabaseConfigured || !supabase) return;
    try {
      const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows found"
      const mfaNeeded = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2';
      console.log('[App] MFA check:', { current: aal?.currentLevel, next: aal?.nextLevel, needed: mfaNeeded });
      setIsMfaRequired(mfaNeeded);
    } catch (err) {
      console.error('[App] MFA check failed:', err);
    }
  };

  // On mount: check for existing Supabase session
  useEffect(() => {
    async function checkSession() {
      if (isSupabaseConfigured && supabase) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const userId = session.user.id;
            console.log('[App] Session check: user logged in. AAL level check...');

            // Fetch AAL level immediately and update state
            const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            const { data: factors } = await supabase.auth.mfa.listFactors();
            const verifiedFactor = factors?.totp?.find(f => f.status === 'verified');
            
            const mfaNeeded = verifiedFactor && aal?.currentLevel === 'aal1';
            console.log('[App] Session check result:', { 
              current: aal?.currentLevel, 
              factorVerified: !!verifiedFactor,
              needed: mfaNeeded 
            });

            setIsMfaRequired(mfaNeeded);
            
            if (!mfaNeeded) {
              // Only load profile if MFA is satisfied
              await useStore.getState().fetchProfile(userId);
              useStore.getState().incrementLoginStreak();
            }
          }
        } catch (err) {
          console.error('[App] Session check failed:', err);
        }
      }
      setAuthReady(true);
    }
    checkSession();

    if (isSupabaseConfigured && supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('[App] Auth state change:', event, session?.user?.id);
        if (event === 'SIGNED_OUT' || !session) {
          const store = useStore.getState();
          if (store.isAuthenticated || store.currentUser) {
            console.log('[App] SIGNED_OUT event. Clearing store...');
            store.logout();
          }
        } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED' || event === 'MFA_CHALLENGE_VERIFIED') && session?.user) {
          const userId = session.user.id;
          const store = useStore.getState();
          
          console.log(`[App] ${event} event for user:`, userId);
          
          if (!store.isAuthenticated || !store.teamId) {
            console.log(`[App] Initializing profile for ${event}...`);
            
            (async () => {
              try {
                // Check MFA status immediately
                await updateMfaStatus();

                // Get current levels
                const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
                const { data: factors } = await supabase.auth.mfa.listFactors();
                const verifiedFactor = factors?.totp?.find(f => f.status === 'verified');

                // If user HAS MFA but isn't at AAL2 yet, block store authentication
                if (verifiedFactor && aal?.currentLevel !== 'aal2') {
                  console.log('[App] MFA required but not reached (AAL1). Blocking store authentication.');
                  setIsMfaRequired(true);
                  // Only clear authentication if it wasn't already set (prevents flicker/loops)
                  if (store.isAuthenticated) {
                    console.log('[App] Resetting store authentication due to AAL1 state');
                    store.setAuthenticated(false);
                  }
                  return;
                }

                console.log('[App] AAL check passed. Setting store as authenticated.');
                setIsMfaRequired(false);
                store.setAuthenticated(true);
                await store.fetchProfile(userId);
                store.incrementLoginStreak();
              } catch (err) {
                console.error('[App] Auth change initialization failed:', err);
              }
            })();
          }
        }
      });
      return () => subscription.unsubscribe();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/Stop SMS Polling based on auth status
  const isAuthenticated = useStore(s => s.isAuthenticated);
  useEffect(() => {
    if (isAuthenticated) {
      startSMSPolling();
    } else {
      stopSMSPolling();
    }
    return () => stopSMSPolling();
  }, [isAuthenticated]);

  if (checking) {
    return <DeepSpaceLoader />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-center" reverseOrder={false} />

      <Routes>
        {/* Marketing Site */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/compare" element={<CRMCompare />} />
        </Route>

        <Route path="/share/:id" element={<LeadShare />} />
        <Route path="/agent/:name" element={<AgentProfile />} />


        {/* Public routes */}
        <Route path="/login" element={<PublicRoute checking={checking} isMfaRequired={isMfaRequired}><Login /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<PublicRoute checking={checking} isMfaRequired={isMfaRequired}><Login defaultMode="signup" /></PublicRoute>} />

        {/* Email confirmation — accessible with or without auth */}
        <Route path="/email-confirmed" element={<PublicRoute checking={checking} isMfaRequired={isMfaRequired}><EmailConfirmed /></PublicRoute>} />
        <Route path="/sign/:token" element={<ContractSigning />} />

        {/* OAuth Callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Team selection — after login, before main app. Also gated by MFA. */}
        <Route path="/team-selection" element={
          <ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}>
            <TeamSelection />
          </ProtectedRoute>
        } />

        {/* Onboarding route — Protected but NOT wrapped in DataSyncWrapper to avoid sync redirect loops */}
        <Route path="/onboarding" element={
          <ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}>
            <Onboarding />
          </ProtectedRoute>
        } />

        <Route path="/contractor-portal/:token" element={
          <ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}>
            <ContractorPortal />
          </ProtectedRoute>
        } />

        <Route path="/tenant-portal/:token" element={
          <ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}>
            <TenantPortal />
          </ProtectedRoute>
        } />

        <Route path="/client-portal" element={
          <ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}>
            <ClientPortal />
          </ProtectedRoute>
        } />

        {/* Protected routes wrapped in a single SupabaseSync to prevent flicker on navigation */}
        <Route element={<ProtectedRoute checking={checking} isMfaRequired={isMfaRequired}><DataSyncWrapper /></ProtectedRoute>}>
          {/* Full-page routes (No Sidebar) */}
          <Route path="/leads/:id/manage" element={<LeadManagement />} />
          
          {/* Main App routes (With Sidebar) */}
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/billing" element={<BillingProfile />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:id" element={<Leads />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/members" element={<Team />} />
            <Route path="/team/calendar" element={<Team />} />
            <Route path="/team/analytics" element={<Team />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/promos" element={<AdminPromos />} />
            <Route path="/ai-training" element={<AITrainingStudio />} />
            <Route path="/automations" element={<AutomationsHub />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/team-chat" element={<Chat />} />
            <Route path="/import" element={<Import />} />
            <Route path="/contracts" element={<Contracts />} />
            <Route path="/contracts/:id" element={<ContractDetail />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="/rent" element={<RentDashboard />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/contractors" element={<Contractors />} />
            <Route path="/leads/:id/share-edit" element={<LeadShareEditor />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/automations" element={<AutomationsHub />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/ai" element={<AISettings />} />
            <Route path="/settings/sms" element={<SMSSettings />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/ai-test" element={<AITest />} />
            <Route path="/sms" element={<SMSInbox />} />
            <Route path="/email" element={<EmailInbox />} />
            <Route path="/notifications" element={<NotificationInbox />} />
            <Route path="/ai-bot" element={<AITest />} />


          </Route>
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
